import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Chip, Divider, IconButton, InputAdornment, LinearProgress, ListItemIcon, Menu, MenuItem,
  Popover, TextField, Tooltip, Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { withSnackbar } from '../../utils/SnackbarHOC';
import Loader from '../../components/Loader/Loader';
import Modal from '../../components/Modal/Modal';
import { AppButton } from '../../components/UI';
import { useAiSearch, useCandidates, useGoMailAuth, useGoMailConfig, useIngest } from './hooks';
import { DATE_RANGES, daysForRange } from './utils';
import GoMailConnect from './components/GoMailConnect';
import CandidateList from './components/CandidateList';
import CandidateDetail from './components/CandidateDetail';
import IngestReport from './components/IngestReport';
import ApiKeysModal from './components/ApiKeysModal';

const PAGE_SIZES = [10, 20, 50, 100];
const INTERVALS = [
  { label: '5 min', value: 5 },
  { label: '10 min', value: 10 },
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '1 h', value: 60 },
  { label: '24 h', value: 1440 },
];
const PHASE_LABEL = {
  fetching: 'Récupération des emails…',
  parsing: 'Analyse des CV par l’IA…',
  done: 'Finalisation…',
};
const RANGE_KEY = 'gomail_date_range';

const readRange = () => {
  try { return localStorage.getItem(RANGE_KEY) || '7d'; } catch (e) { return '7d'; }
};

const pillSx = (active) => ({
  height: 28,
  fontSize: '0.76rem',
  fontWeight: 700,
  borderRadius: '8px',
  cursor: 'pointer',
  bgcolor: active ? '#03407b' : '#fff',
  color: active ? '#fff' : '#475569',
  border: `1px solid ${active ? '#03407b' : '#e2e8f0'}`,
  '&:hover': { bgcolor: active ? '#02357a' : '#f0f7fd' },
  '& .MuiChip-icon': { color: 'inherit', fontSize: 15 },
});

const counterSx = { ml: 0.75, px: 0.75, borderRadius: '6px', fontSize: '0.7rem', fontWeight: 800, bgcolor: 'rgba(3,64,123,0.1)' };

const iconBtnSx = {
  width: 36,
  height: 36,
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  bgcolor: '#fff',
  color: '#475569',
  '&:hover': { bgcolor: '#f0f7fd', color: '#03407b' },
};

const Workspace = ({ auth, snackbarShowMessage }) => {
  const { email, isAdmin, logout } = auth;
  const history = useHistory();
  const { candidates, setCandidates, stats, loading, total, fetchCandidates, fetchStats, actions } = useCandidates(email);
  const search = useAiSearch(email);
  const { interval, updateInterval } = useGoMailConfig(true);

  const [dateRange, setDateRange] = useState(readRange);
  const [filter, setFilter] = useState(null); // 'not_viewed' | 'added_to_crm'
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirm, setConfirm] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [keysOpen, setKeysOpen] = useState(false);
  const searchRef = useRef(null);

  const serverFilter = useMemo(() => ({
    viewed: filter === 'not_viewed' ? false : undefined,
    addedToCrm: filter === 'added_to_crm' ? true : undefined,
  }), [filter]);

  const reload = useCallback((overrides = {}) => {
    fetchCandidates({ pageSize, page, archived: showArchived, ...serverFilter, ...overrides });
    fetchStats();
    actions.archivedCount().then(setArchivedCount).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCandidates, fetchStats, pageSize, page, showArchived, serverFilter]);

  const ingest = useIngest(true, (summary) => {
    reload();
    if (summary.indexed > 0) snackbarShowMessage(`${summary.indexed} nouveau(x) CV analysé(s).`, 'success');
  });

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => reload(), 30000);
    return () => clearInterval(id);
  }, [reload]);

  useEffect(() => {
    try { localStorage.setItem(RANGE_KEY, dateRange); } catch (e) { /* ignore */ }
  }, [dateRange]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const resetSelection = () => {
    setSelectedIds(new Set());
    setSelected(null);
  };

  // One view at a time: 'all' | 'not_viewed' | 'added_to_crm' | 'archived'.
  const view = showArchived ? 'archived' : filter || 'all';

  const selectView = (next) => {
    const target = view === next ? 'all' : next;
    const archived = target === 'archived';
    const value = archived || target === 'all' ? null : target;
    setFilter(value);
    setShowArchived(archived);
    setPage(0);
    resetSelection();
    search.reset();
    fetchCandidates({
      pageSize,
      page: 0,
      archived,
      viewed: value === 'not_viewed' ? false : undefined,
      addedToCrm: value === 'added_to_crm' ? true : undefined,
    });
  };

  const goToPage = (p) => {
    setPage(p);
    setSelectedIds(new Set());
    fetchCandidates({ pageSize, page: p, archived: showArchived, ...serverFilter });
  };

  const changePageSize = (size) => {
    setPageSize(size);
    setPage(0);
    fetchCandidates({ pageSize: size, page: 0, archived: showArchived, ...serverFilter });
  };

  const mode = search.active ? 'search' : 'browse';

  const visible = useMemo(() => {
    if (mode === 'search') return search.results;
    const days = daysForRange(dateRange);
    if (!days) return candidates;
    const cutoff = Date.now() - days * 86400000;
    return candidates.filter((c) => {
      const t = new Date(c.date).getTime();
      return !Number.isNaN(t) && t >= cutoff;
    });
  }, [mode, search.results, candidates, dateRange]);

  const patchCandidate = useCallback((updated) => {
    setCandidates((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    search.setResults((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setSelected((prev) => (prev?.id === updated.id ? updated : prev));
  }, [setCandidates, search]);

  const handleSelect = useCallback(async (c) => {
    setSelected(c);
    if (!c.viewed) {
      try {
        await actions.markViewed(c.id);
        patchCandidate({ ...c, viewed: true });
        fetchStats();
      } catch (e) { /* keep unread */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchCandidate, fetchStats]);

  // Keyboard: ↑/↓ moves through the list, Échap closes the profile (ignored while typing or in a dialog).
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || e.target?.isContentEditable) return;
      if (document.querySelector('.MuiDialog-root')) return;
      if (e.key === 'Escape' && selected) {
        setSelected(null);
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (visible.length === 0) return;
      e.preventDefault();
      const idx = selected ? visible.findIndex((c) => c.id === selected.id) : -1;
      const next = e.key === 'ArrowDown' ? Math.min(visible.length - 1, idx + 1) : Math.max(0, idx - 1);
      if (visible[next] && visible[next].id !== selected?.id) handleSelect(visible[next]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, selected, handleSelect]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = (checked) => setSelectedIds(checked ? new Set(visible.map((c) => c.id)) : new Set());

  const ask = (title, message, action, danger = false) => setConfirm({ title, message, action, danger });

  const runConfirm = async () => {
    setConfirmLoading(true);
    try {
      await confirm.action();
      setConfirm(null);
    } catch (e) {
      snackbarShowMessage(e?.message || 'Action impossible.', 'error');
    } finally {
      setConfirmLoading(false);
    }
  };

  const afterMutation = (ids) => {
    setSelectedIds(new Set());
    if (selected && ids.includes(selected.id)) setSelected(null);
    if (mode === 'search') search.setResults((prev) => prev.filter((c) => !ids.includes(c.id)));
    reload();
  };

  const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;

  const archiveOne = (id) => ask('Archiver ce candidat ?', 'Il sera déplacé dans les archives et pourra être restauré à tout moment.', async () => {
    await actions.archive(id);
    afterMutation([id]);
  });

  const restoreOne = async (id) => {
    try {
      await actions.unarchive(id);
      afterMutation([id]);
      snackbarShowMessage('Candidat restauré.', 'success');
    } catch (e) {
      snackbarShowMessage('Restauration impossible.', 'error');
    }
  };

  const deleteOne = (id) => ask('Supprimer définitivement ?', 'Le candidat et son CV seront supprimés. Cette action est irréversible.', async () => {
    await actions.remove(id);
    afterMutation([id]);
  }, true);

  const bulkArchive = () => {
    const ids = [...selectedIds];
    ask(`Archiver ${plural(ids.length, 'candidat')} ?`, 'Ils seront déplacés dans les archives.', async () => {
      await actions.bulkArchive(ids);
      afterMutation(ids);
    });
  };

  const bulkRestore = async () => {
    const ids = [...selectedIds];
    try {
      for (const id of ids) await actions.unarchive(id);
      afterMutation(ids);
      snackbarShowMessage(`${plural(ids.length, 'candidat')} restauré(s).`, 'success');
    } catch (e) {
      snackbarShowMessage('Restauration impossible.', 'error');
    }
  };

  const bulkDelete = () => {
    const ids = [...selectedIds];
    ask(`Supprimer ${plural(ids.length, 'candidat')} ?`, 'Cette action est irréversible.', async () => {
      await actions.bulkDelete(ids);
      afterMutation(ids);
    }, true);
  };

  const triggerIngest = async () => {
    const days = daysForRange(dateRange);
    const dateFrom = days ? new Date(Date.now() - days * 86400000).toISOString().split('T')[0] : undefined;
    try {
      const summary = await ingest.trigger(dateFrom);
      reload();
      if (summary) setReport(summary);
    } catch (e) {
      snackbarShowMessage('La récupération des emails a échoué.', 'error');
    }
  };

  const openCrmUrl = (url) => {
    if (!url) return;
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin === window.location.origin) {
        history.push(`${u.pathname}${u.search}`);
        return;
      }
    } catch (e) { /* fall through */ }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const submitSearch = () => {
    if (!search.query.trim()) return;
    setSelected(null);
    setSelectedIds(new Set());
    search.search(search.query);
  };

  const exitSearch = () => {
    search.reset();
    setSelected(null);
    setSelectedIds(new Set());
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const listTitle = mode === 'search'
    ? `Résultats pour « ${search.query} »`
    : showArchived ? 'Archives' : filter === 'not_viewed' ? 'Candidats non consultés' : filter === 'added_to_crm' ? 'Candidats ajoutés au CRM' : 'Candidats reçus';
  const countLabel = mode === 'search' ? visible.length : total;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%', minWidth: 0, bgcolor: '#f5f7fa' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', px: { xs: 2, md: 3 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 220 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: '#ebf4fb', color: '#03407b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MailOutlineIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>GoMail</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>CV reçus par email, analysés par l’IA</Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 260, maxWidth: 620, mx: 'auto' }}>
          <TextField
            inputRef={searchRef}
            fullWidth
            size="small"
            placeholder="Recherche IA : « développeur React 5 ans à Paris »  (Ctrl+K)"
            value={search.query}
            onChange={(e) => search.setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitSearch();
              if (e.key === 'Escape' && search.active) exitSearch();
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8fafc' } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    {search.active ? (
                      <Tooltip title="Retour à la liste">
                        <IconButton size="small" onClick={exitSearch} sx={{ ml: -0.5 }}><ArrowBackIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    ) : (
                      <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                    )}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <AppButton size="small" startIcon={<AutoAwesomeOutlinedIcon />} loading={search.loading} disabled={!search.query.trim()} onClick={submitSearch} sx={{ height: 28, mr: -0.75 }}>
                      Chercher
                    </AppButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Analyser les nouveaux CV reçus dans la boîte mail sur la période sélectionnée">
            <span>
              <AppButton
                startIcon={<SyncIcon sx={{ animation: ingest.running ? 'gomail-spin 1s linear infinite' : 'none', '@keyframes gomail-spin': { to: { transform: 'rotate(360deg)' } } }} />}
                disabled={ingest.running}
                onClick={triggerIngest}
              >
                {ingest.running ? 'Récupération…' : 'Récupérer les candidats'}
              </AppButton>
            </span>
          </Tooltip>
          <Tooltip title="Récupération automatique">
            <IconButton onClick={(e) => setSettingsAnchor(e.currentTarget)} sx={iconBtnSx}><SettingsOutlinedIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={email}>
            <IconButton onClick={(e) => setAccountAnchor(e.currentTarget)} sx={iconBtnSx}><AccountCircleOutlinedIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {ingest.running && (
        <Box sx={{ px: { xs: 2, md: 3 }, py: 0.75, bgcolor: '#f0f7fd', borderBottom: '1px solid #e2edf7' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#03407b' }}>{PHASE_LABEL[ingest.phase] || 'Traitement…'}</Typography>
            {ingest.total > 0 && (
              <Typography sx={{ fontSize: '0.74rem', color: '#64748b' }}>
                {ingest.processed}/{ingest.total} · {Math.round((ingest.processed / ingest.total) * 100)}%
              </Typography>
            )}
          </Box>
          <LinearProgress
            variant={ingest.total > 0 ? 'determinate' : 'indeterminate'}
            value={ingest.total > 0 ? Math.min(100, (ingest.processed / ingest.total) * 100) : undefined}
            sx={{ height: 5, borderRadius: 3, bgcolor: '#dbeafe', '& .MuiLinearProgress-bar': { bgcolor: '#03407b', borderRadius: 3 } }}
          />
        </Box>
      )}

      {/* Body: the list takes the full width; the profile opens on the right when a candidate is selected. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          p: { xs: 1.5, md: 2.5 },
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: selected ? 'minmax(0, 1.5fr) minmax(400px, 1fr)' : 'minmax(0,1fr)' },
          gap: 2,
        }}
      >
        <Box sx={{ minHeight: { xs: 480, lg: 0 }, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {mode === 'search' ? (
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eef2f7', display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', background: 'linear-gradient(90deg, #f0f7fd 0%, #fff 70%)' }}>
              <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: '#03407b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography noWrap sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>Recherche IA : « {search.query} »</Typography>
                <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>
                  {search.loading ? 'Analyse des profils…' : `${plural(visible.length, 'candidat')} classé${visible.length > 1 ? 's' : ''} par pertinence, du plus au moins adapté`}
                </Typography>
              </Box>
              <AppButton size="small" variant="secondary" startIcon={<ArrowBackIcon />} onClick={exitSearch}>Retour à la liste</AppButton>
            </Box>
          ) : (
            <Box sx={{ px: 2, pt: 1.5, pb: 1.25, borderBottom: '1px solid #eef2f7', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Tooltip title="Période de réception des CV">
                  <EventOutlinedIcon sx={{ fontSize: 17, color: '#94a3b8', mr: 0.25 }} />
                </Tooltip>
                {DATE_RANGES.map((r) => (
                  <Chip key={r.value} label={r.label} size="small" onClick={() => setDateRange(r.value)} sx={pillSx(dateRange === r.value)} />
                ))}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                {[
                  { key: 'all', label: 'Tous' },
                  { key: 'not_viewed', label: 'Non consultés', count: stats.not_viewed || 0 },
                  { key: 'added_to_crm', label: 'Dans le CRM', count: stats.added_to_crm || 0 },
                  { key: 'archived', label: 'Archivés', count: archivedCount, icon: <ArchiveOutlinedIcon /> },
                ].map((v) => (
                  <Chip
                    key={v.key}
                    size="small"
                    icon={v.icon}
                    onClick={() => selectView(v.key)}
                    sx={{
                      ...pillSx(view === v.key),
                      '& .counter': { ...counterSx, bgcolor: view === v.key ? 'rgba(255,255,255,0.22)' : 'rgba(3,64,123,0.1)' },
                    }}
                    label={<>{v.label}{v.count !== undefined && <Box component="span" className="counter">{v.count}</Box>}</>}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                <Typography noWrap sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
                  {listTitle}
                  <Box component="span" sx={{ ml: 1, fontSize: '0.78rem', fontWeight: 600, color: '#64748b' }}>{plural(countLabel, 'candidat')}</Box>
                </Typography>
                {visible.length > 0 && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
                    Astuce : ↑ ↓ pour passer d'un candidat à l'autre, Échap pour fermer
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <CandidateList
              candidates={visible}
              mode={mode}
              loading={mode === 'search' ? search.loading : loading}
              selectedId={selected?.id ?? null}
              selectedIds={selectedIds}
              showArchived={showArchived}
              compact={!!selected}
              onSelect={handleSelect}
              onToggleSelect={toggleSelect}
              onToggleAll={toggleAll}
              onArchive={archiveOne}
              onRestore={restoreOne}
              onDelete={deleteOne}
            />
          </Box>

          {selectedIds.size > 0 ? (
            <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid #e2e8f0', bgcolor: '#f0f7fd', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#03407b', mr: 'auto' }}>
                {plural(selectedIds.size, 'candidat')} sélectionné{selectedIds.size > 1 ? 's' : ''}
              </Typography>
              <AppButton size="small" variant="ghost" onClick={() => setSelectedIds(new Set())}>Désélectionner</AppButton>
              {showArchived ? (
                <>
                  <AppButton size="small" variant="outline" startIcon={<UnarchiveOutlinedIcon />} onClick={bulkRestore}>Restaurer</AppButton>
                  <AppButton size="small" variant="danger" startIcon={<DeleteOutlinedIcon />} onClick={bulkDelete}>Supprimer</AppButton>
                </>
              ) : (
                <AppButton size="small" variant="outline" startIcon={<ArchiveOutlinedIcon />} onClick={bulkArchive}>Archiver</AppButton>
              )}
            </Box>
          ) : mode === 'browse' && total > 0 && (
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid #eef2f7', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.74rem', color: '#64748b', mr: 0.5 }}>Par page</Typography>
                {PAGE_SIZES.map((s) => (
                  <Chip key={s} size="small" label={s} onClick={() => changePageSize(s)} sx={{ ...pillSx(pageSize === s), height: 24, minWidth: 34 }} />
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>Page {page + 1} / {totalPages}</Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => goToPage(page - 1)} sx={{ ...iconBtnSx, width: 30, height: 30 }}><ChevronLeftIcon fontSize="small" /></IconButton>
                <IconButton size="small" disabled={page >= totalPages - 1} onClick={() => goToPage(page + 1)} sx={{ ...iconBtnSx, width: 30, height: 30 }}><ChevronRightIcon fontSize="small" /></IconButton>
              </Box>
            </Box>
          )}
        </Box>

        {selected && (
          <Box
            sx={{
              minHeight: { xs: 520, lg: 0 },
              bgcolor: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '14px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 8px 28px rgba(15,23,42,0.06)',
              animation: 'gomail-panel-in .22s ease-out',
              '@keyframes gomail-panel-in': { from: { opacity: 0, transform: 'translateX(12px)' }, to: { opacity: 1, transform: 'none' } },
            }}
          >
            <CandidateDetail
              candidate={selected}
              actions={actions}
              onCandidateChange={patchCandidate}
              onAddedToCrm={(c) => { patchCandidate(c); fetchStats(); }}
              onOpenCrmUrl={openCrmUrl}
              onClose={() => setSelected(null)}
              snackbarShowMessage={snackbarShowMessage}
            />
          </Box>
        )}
      </Box>

      <Popover
        open={!!settingsAnchor}
        anchorEl={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, p: 2, width: 280, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 12px 32px rgba(15,23,42,0.12)' } } }}
      >
        <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a' }}>Récupération automatique</Typography>
        <Typography sx={{ fontSize: '0.76rem', color: '#64748b', mt: 0.25, mb: 1.5 }}>
          Fréquence à laquelle GoMail analyse seul les nouveaux emails reçus.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
          {INTERVALS.map((it) => (
            <Chip
              key={it.value}
              label={it.label}
              onClick={async () => {
                try {
                  await updateInterval(it.value);
                  snackbarShowMessage(`Récupération automatique : toutes les ${it.label}.`, 'success');
                } catch (e) {
                  snackbarShowMessage('Impossible de modifier la fréquence.', 'error');
                }
                setSettingsAnchor(null);
              }}
              sx={{ ...pillSx(interval === it.value), justifyContent: 'center' }}
            />
          ))}
        </Box>
      </Popover>

      <Menu
        anchorEl={accountAnchor}
        open={!!accountAnchor}
        onClose={() => setAccountAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 240, borderRadius: '12px', border: '1px solid #e2e8f0' } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Boîte connectée</Typography>
          <Typography noWrap sx={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a' }}>{email}</Typography>
        </Box>
        <Divider />
        {isAdmin && (
          <MenuItem onClick={() => { setKeysOpen(true); setAccountAnchor(null); }} sx={{ fontSize: '0.86rem' }}>
            <ListItemIcon><AdminPanelSettingsOutlinedIcon fontSize="small" /></ListItemIcon>
            Administration (clés API)
          </MenuItem>
        )}
        <MenuItem onClick={() => { setAccountAnchor(null); logout(); }} sx={{ fontSize: '0.86rem', color: '#dc2626' }}>
          <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#dc2626' }} /></ListItemIcon>
          Déconnecter la boîte mail
        </MenuItem>
      </Menu>

      <Modal
        open={!!confirm}
        onClose={() => !confirmLoading && setConfirm(null)}
        title={confirm?.title}
        maxWidth="xs"
        actions={
          <>
            <AppButton variant="secondary" onClick={() => setConfirm(null)} disabled={confirmLoading}>Annuler</AppButton>
            <AppButton variant={confirm?.danger ? 'danger' : 'primary'} loading={confirmLoading} onClick={runConfirm}>Confirmer</AppButton>
          </>
        }
      >
        <Typography sx={{ fontSize: '0.88rem', color: '#334155' }}>{confirm?.message}</Typography>
      </Modal>

      <IngestReport report={report} onClose={() => setReport(null)} />
      <ApiKeysModal open={keysOpen} onClose={() => setKeysOpen(false)} snackbarShowMessage={snackbarShowMessage} />
    </Box>
  );
};

const GoMail = ({ snackbarShowMessage }) => {
  const auth = useGoMailAuth();

  if (auth.loading) return <Loader />;

  if (!auth.isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: '#f5f7fa' }}>
        <GoMailConnect connecting={auth.connecting} error={auth.error} onLogin={auth.login} />
      </Box>
    );
  }

  return <Workspace auth={auth} snackbarShowMessage={snackbarShowMessage} />;
};

export default withSnackbar(GoMail);
